import { apiClient } from './apiClient';
import {
  FALLBACK_RATE,
  type CurrencyCode,
  type CurrencySettings,
  type FxRateSnapshot,
} from '../types/currency.types';

function extractSettings(raw: any, fallbackCode?: CurrencyCode): CurrencySettings {
  const d = raw?.data ?? raw;
  return {
    defaultCurrency: d?.defaultCurrency ?? d?.default_currency ?? fallbackCode ?? 'USD',
    rate: d?.rate ?? d?.fx_rate ?? FALLBACK_RATE,
  };
}

// ── IP geolocation ────────────────────────────────────────────────────
// Country is cached in localStorage so we don't hammer the geo API on
// every page load. 24h TTL strikes a balance between freshness and
// avoiding the free-tier quota.
const GEO_CACHE_KEY = 'ca_geo_country';
const GEO_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface GeoCache { country: string; expiresAt: number }

function readGeoCache(): string | null {
  try {
    const raw = localStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GeoCache;
    if (parsed.expiresAt > Date.now() && parsed.country) return parsed.country;
  } catch { /* corrupt or SSR */ }
  return null;
}

function writeGeoCache(country: string): void {
  try {
    const payload: GeoCache = { country, expiresAt: Date.now() + GEO_CACHE_TTL_MS };
    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(payload));
  } catch { /* noop */ }
}

export const currencyService = {
  async getPublicSettings(): Promise<CurrencySettings> {
    try {
      const res = await apiClient.get('/settings/currency');
      const d = (res as any);
      if (d.defaultCurrency || d.default_currency || d.data) {
        return extractSettings(d);
      }
    } catch { /* fall through */ }

    const liveRate = await this.fetchLiveRate();
    return {
      defaultCurrency: 'USD',
      rate: liveRate ?? FALLBACK_RATE,
    };
  },

  async fetchLiveRate(): Promise<FxRateSnapshot | null> {
    try {
      const res = await fetch('https://open.er-api.com/v6/latest/USD');
      if (!res.ok) return null;
      const data = await res.json();
      const ngnPerUsd = data?.rates?.NGN;
      if (!ngnPerUsd || typeof ngnPerUsd !== 'number') return null;
      return {
        base: 'NGN',
        ngnPerUsd: Math.round(ngnPerUsd),
        fetchedAtISO: new Date().toISOString(),
        source: 'open.er-api.com',
      };
    } catch {
      return null;
    }
  },

  async setDefaultCurrency(code: CurrencyCode): Promise<CurrencySettings> {
    const res = await apiClient.patch('/admin/settings/currency', {
      defaultCurrency: code,
    });
    const raw = res as any;
    if (raw.success === false) {
      throw new Error(raw.error?.message || 'Failed to update default currency');
    }
    return extractSettings(raw, code);
  },

  /**
   * Best-effort IP-based country detection. Returns an ISO 3166-1 alpha-2
   * code (e.g. "NG", "US") or null if every probe fails. Results are
   * cached in localStorage for 24h to limit upstream traffic.
   *
   * Tries the backend first (so a self-hosted MaxMind / Cloudflare header
   * lookup can override the public service), then falls back to a free
   * public geo API.
   */
  async detectCountry(): Promise<string | null> {
    const cached = readGeoCache();
    if (cached) return cached;

    // 1. Backend (preferred — uses the request IP from our own edge).
    try {
      const res = await apiClient.get('/geo/country');
      const d = (res as any)?.data ?? res;
      const country: string | undefined =
        d?.countryCode ?? d?.country_code ?? d?.country;
      if (country && typeof country === 'string') {
        writeGeoCache(country.toUpperCase());
        return country.toUpperCase();
      }
    } catch { /* backend may not implement it yet */ }

    // 2. Public fallback — ipapi.co (free, no key, CORS-enabled).
    try {
      const res = await fetch('https://ipapi.co/json/');
      if (res.ok) {
        const data = await res.json();
        const country: string | undefined = data?.country_code;
        if (country && typeof country === 'string') {
          writeGeoCache(country.toUpperCase());
          return country.toUpperCase();
        }
      }
    } catch { /* offline or blocked */ }

    return null;
  },

  async refreshRate(): Promise<FxRateSnapshot> {
    const res = await apiClient.post('/admin/settings/currency/refresh-rate');
    const raw = res as any;
    if (raw.success === false) {
      throw new Error(raw.error?.message || 'Failed to refresh rate');
    }
    const d = raw?.data ?? raw;
    return d?.rate ?? d?.fx_rate ?? FALLBACK_RATE;
  },
};
